import { useCallback, useMemo, useRef, useState } from "react";
import type { FlatEvent, Issue, SessionDetail } from "@backstory/protocol";
import { formatClock } from "../lib/format";
import { captureSummary, LEVEL_EFFECT, LEVEL_LABEL, REASON_LABEL } from "../lib/capture";

export interface TimelineLaneProps {
  session: SessionDetail;
  events: FlatEvent[];
  /** Detected issues for this session (optional; from GET /v1/sessions/{id}/issues). */
  issues?: Issue[];
  /** Playhead position, relative ms. */
  time: number;
  onSeek: (relMs: number) => void;
  /** Collapse to the single classic lane (used by the share page). */
  compact?: boolean;
}

export interface LaneMark {
  key: string;
  /** Absolute ms. */
  ts: number;
  lane: LaneId;
  kind: string;
  label: string;
}

export type LaneId = "interaction" | "issues" | "errors" | "network" | "console" | "vitals" | "state";

export const LANES: Array<{ id: LaneId; label: string }> = [
  { id: "interaction", label: "Clicks & pages" },
  { id: "issues", label: "Issues" },
  { id: "errors", label: "Errors" },
  { id: "network", label: "Failed requests" },
  { id: "console", label: "Console" },
  { id: "vitals", label: "Vitals" },
  { id: "state", label: "State" },
];

/** Build marks for every lane from flattened events and issues. */
export function buildMarks(events: FlatEvent[], issues: Issue[] | undefined, startTs: number): LaneMark[] {
  const marks: LaneMark[] = [];
  for (const e of events) {
    const key = `${e.seq}-${e.idx}`;
    const kind = laneKind(e);
    if (kind === "click" || kind === "navigation") marks.push({ key, ts: e.ts, lane: "interaction", kind, label: kind === "click" ? `Click ${e.name ?? ""}`.trim() : `Page ${e.url ?? ""}` });
    else if (kind === "error") marks.push({ key, ts: e.ts, lane: "errors", kind: "error", label: `Error: ${e.value ?? e.name ?? ""}` });
    else if (kind === "network") marks.push({ key, ts: e.ts, lane: "network", kind: "network", label: `${e.name ?? "GET"} ${e.url ?? ""} → ${e.status ?? "failed"}` });
    else if (kind === "console") marks.push({ key, ts: e.ts, lane: "console", kind: "console", label: `console.${e.subtype}: ${e.value ?? ""}` });
    if (e.type === "vital") {
      const rating = String(e.props?.["rating"] ?? e.subtype ?? "good");
      marks.push({ key: `v-${key}`, ts: e.ts, lane: "vitals", kind: `vital-${rating}`, label: `${e.name ?? "vital"} ${e.value ?? e.durationMs ?? ""} (${rating})` });
    }
    if (e.type === "state") marks.push({ key: `s-${key}`, ts: e.ts, lane: "state", kind: "state", label: `${e.subtype ?? e.name ?? "state"}: ${e.value ?? ""}` });
  }
  for (const i of issues ?? []) marks.push({ key: `i-${i.id}`, ts: startTs + i.tStart, lane: "issues", kind: `issue-${i.severity}`, label: `${i.kind.replace(/_/g, " ")} (${i.severity})` });
  marks.sort((a, b) => a.ts - b.ts);
  return marks;
}

/** Event density per bucket (0..1) for the strip under the lanes. */
export function density(events: FlatEvent[], startTs: number, durationMs: number, buckets = 120): number[] {
  const out = new Array<number>(buckets).fill(0);
  if (!events.length || durationMs <= 0) return out;
  for (const e of events) {
    const i = Math.min(buckets - 1, Math.max(0, Math.floor(((e.ts - startTs) / durationMs) * buckets)));
    out[i] = (out[i] ?? 0) + 1;
  }
  const max = Math.max(1, ...out);
  return out.map((v) => v / max);
}

export function TimelineLane({ session, events, issues, time, onSeek, compact }: TimelineLaneProps) {
  const start = new Date(session.startedAt).getTime();
  const duration = Math.max(1, session.durationMs);
  const pct = (ts: number) => `${Math.min(100, Math.max(0, ((ts - start) / duration) * 100))}%`;
  const marks = useMemo(() => buildMarks(events, issues, start), [events, issues, start]);
  const dens = useMemo(() => density(events, start, duration), [events, start, duration]);
  const capture = useMemo(() => captureSummary(session, events), [session, events]);
  const [tip, setTip] = useState<{ x: number; text: string } | null>(null);
  const [focused, setFocused] = useState<number>(-1);
  const [expanded, setExpanded] = useState(true);
  const rootRef = useRef<HTMLDivElement>(null);

  const seekFromPointer = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const frac = (e.clientX - rect.left) / rect.width;
      onSeek(frac * duration);
    },
    [onSeek, duration],
  );

  // Keyboard: ArrowRight/Left move between markers, Enter seeks to the focused one.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!marks.length) return;
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const nowAbs = start + time;
      let i: number;
      if (e.key === "ArrowRight") {
        i = focused >= 0 ? Math.min(marks.length - 1, focused + 1) : marks.findIndex((m) => m.ts > nowAbs);
        if (i === -1) i = marks.length - 1;
      } else {
        i = focused >= 0 ? Math.max(0, focused - 1) : marks.map((m) => m.ts).filter((ts) => ts < nowAbs).length - 1;
        if (i < 0) i = 0;
      }
      setFocused(i);
      const m = marks[i]!;
      setTip({ x: ((m.ts - start) / duration) * 100, text: `${formatClock(m.ts - start)} · ${m.label}` });
      onSeek(m.ts - start);
    } else if (e.key === "Escape") {
      setTip(null);
      setFocused(-1);
    }
  };

  const gapEls = session.gaps.map((g, i) => {
    const after = session.chunks.find((c) => c.seq > g.toSeq);
    if (!after) return null;
    const end = after.firstTs;
    const s = end - g.ms;
    return <div key={`g${i}`} className="gap" style={{ left: pct(s), width: `calc(${pct(end)} - ${pct(s)})` }} title={`Not recorded: ${g.reason}`} />;
  });

  const laneEl = (laneId: LaneId | null, className = "lane") => (
    <div
      className={className}
      role={laneId ? undefined : "slider"}
      aria-label={laneId ? LANES.find((l) => l.id === laneId)?.label : "Timeline"}
      aria-valuemin={laneId ? undefined : 0}
      aria-valuemax={laneId ? undefined : duration}
      aria-valuenow={laneId ? undefined : Math.round(time)}
      tabIndex={laneId ? -1 : 0}
      onClick={seekFromPointer}
      onMouseLeave={() => setTip(null)}
      onKeyDown={laneId ? undefined : onKeyDown}
    >
      {laneId === null || laneId === "interaction" ? gapEls : null}
      {marks
        .filter((m) => (laneId ? m.lane === laneId : m.lane === "interaction" || m.lane === "errors" || m.lane === "network" || m.lane === "console"))
        .map((m) => {
          const idx = marks.indexOf(m);
          return (
            <div
              key={m.key}
              className={`mark ${m.kind} ${idx === focused ? "focused" : ""}`}
              style={{ left: pct(m.ts) }}
              data-testid="lane-mark"
              data-lane={m.lane}
              title={`${m.label} @ ${formatClock(m.ts - start)}`}
              onMouseEnter={() => setTip({ x: ((m.ts - start) / duration) * 100, text: `${formatClock(m.ts - start)} · ${m.label}` })}
              onClick={(e) => {
                e.stopPropagation();
                setFocused(idx);
                onSeek(m.ts - start);
              }}
            />
          );
        })}
      <div className="playhead" style={{ left: pct(start + time) }} />
    </div>
  );

  if (compact) return laneEl(null);

  return (
    <div className={`timeline-details${expanded ? " expanded" : ""}`} data-testid="timeline-lanes">
      <button type="button" className="timeline-details-toggle" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
        <span><span aria-hidden="true">{expanded ? "▾" : "▸"}</span> Timeline details</span>
        <small>{marks.length.toLocaleString()} marker{marks.length === 1 ? "" : "s"}</small>
      </button>
      {expanded ? <div className="lanes" ref={rootRef} style={{ position: "relative" }}>
      {tip && (
        <div className="lane-tip" style={{ left: `calc(78px + (100% - 78px) * ${tip.x / 100})`, top: -4 }} role="status">
          {tip.text}
        </div>
      )}
      <div className="lane-row">
        <span className="lane-label">Activity</span>
        <div className="lane" onClick={seekFromPointer} aria-hidden="true">
          <div className="density">
            {dens.map((v, i) => (
              <i key={i} style={{ opacity: 0.08 + v * 0.7 }} />
            ))}
          </div>
          <div className="playhead" style={{ left: pct(start + time) }} />
        </div>
      </div>
      {LANES.filter((l) => l.id === "interaction" || marks.some((m) => m.lane === l.id)).map((l) => (
        <div className="lane-row" key={l.id}>
          <span className="lane-label">{l.label}</span>
          {laneEl(l.id, l.id === "interaction" ? "lane" : "lane")}
        </div>
      ))}
      {/* Only worth a row when capture actually varied: an all-healthy bar
          would be a solid stripe that says nothing the badge does not. */}
      {capture.worst !== "HEALTHY" && capture.reported ? (
        <div className="lane-row">
          <span className="lane-label">Capture</span>
          <div className="lane capture-lane" onClick={seekFromPointer} onMouseLeave={() => setTip(null)}>
            {capture.spans.map((s) => {
              const text = `${LEVEL_LABEL[s.level]}${s.reason ? ` · ${REASON_LABEL[s.reason]}` : ""}${s.level === "HEALTHY" ? "" : ` — ${LEVEL_EFFECT[s.level]}`}`;
              return (
                <div
                  key={`${s.fromMs}-${s.level}`}
                  className={`capture-span level-${s.level.toLowerCase()}`}
                  style={{ left: pct(start + s.fromMs), width: `calc(${pct(start + s.toMs)} - ${pct(start + s.fromMs)})` }}
                  data-testid="capture-span"
                  data-level={s.level}
                  title={`${formatClock(s.fromMs)}–${formatClock(s.toMs)} · ${text}`}
                  onMouseEnter={() => setTip({ x: (s.fromMs / duration) * 100, text: `${formatClock(s.fromMs)} · ${text}` })}
                />
              );
            })}
            <div className="playhead" style={{ left: pct(start + time) }} />
          </div>
        </div>
      ) : null}
      {/* Keyboard-navigable slider representing the whole timeline */}
      <div className="lane-row">
        <span className="lane-label">All</span>
        {laneEl(null)}
      </div>
      </div> : null}
    </div>
  );
}

export function laneKind(e: FlatEvent): string | null {
  if (e.type === "pointer" && e.subtype === "click") return "click";
  if (e.type === "error") return "error";
  if (e.type === "navigation") return "navigation";
  if (e.type === "console" && (e.subtype === "error" || e.subtype === "warn")) return "console";
  if (e.type === "network" && ((e.status ?? 0) >= 400 || e.props?.["failed"] === true)) return "network";
  return null;
}
